import { HonorBadge } from "@/components/HonorBadge";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Mail, Send, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar } from "@/components/UserBits";
import { useIsVip, useVipIds } from "@/lib/vip";
import { SupporterBadge } from "@/components/RoleBadge";
import { timeAgo } from "@/lib/timeAgo";
import { spooky } from "@/components/halloween/spooky";

const POLL_MS = 4000;

const StoryRef = ({ s, mine }) => (
  <span className={`flex items-center gap-2 mb-1.5 text-[11px] font-semibold ${mine ? "text-pink-100" : "text-slate-500"}`} data-testid={`msg-story-ref-${s.id}`}>
    <span className="w-9 h-14 rounded-md overflow-hidden bg-black/10 shrink-0">
      {s.kind === "video" ? <video src={imgSrc(s.media)} muted preload="metadata" className="w-full h-full object-cover" /> : <img src={imgSrc(s.media)} alt="" className="w-full h-full object-cover" />}
    </span>
    Antwort auf {mine ? "die" : "deine"} Story
  </span>
);

export default function Messages() {
  const [list, setList] = useState(null);
  const vipIds = useVipIds();
  useEffect(() => { api.get("/messages").then(({ data }) => setList(data)).catch(() => setList([])); }, []);
  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="messages-page">
      <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-2"><Mail className="w-7 h-7 text-rose-500" /> Nachrichten</h1>
      <div className="bg-white rounded-3xl p-2 sm:p-3 border border-pink-100">
        {!list ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>
          : list.length === 0 ? <p className="text-center text-sm text-slate-400 py-12 px-4" data-testid="messages-empty">Noch keine Nachrichten. Öffne das Profil eines Freundes und tippe auf „Nachricht“.</p>
          : <ul className="space-y-1">{list.map((c) => (
            <li key={c.user.user_id}><Link to={`/nachrichten/${c.user.user_id}`} data-testid={`conversation-${c.user.user_id}`} className={`flex items-center gap-3 p-3 rounded-2xl transition-colors ${c.unread ? "bg-rose-50" : "hover:bg-rose-50/60"}`}>
              <UserAvatar userId={c.user.user_id} name={c.user.name} picture={c.user.picture} size="w-12 h-12" />
              <div className="min-w-0 flex-1">
                <div className="flex justify-between gap-2"><p className="font-semibold text-sm min-w-0 flex items-center gap-x-1 gap-y-0.5 flex-wrap"><span className="truncate max-w-full">{c.user.name}</span>{vipIds.has(c.user.user_id) && <SupporterBadge chip testId={`conv-supporter-${c.user.user_id}`} />}<HonorBadge userId={c.user.user_id} /></p><span className="text-[11px] text-slate-400 shrink-0">{timeAgo(c.last_at)}</span></div>
                <p className={`text-sm truncate ${c.unread ? "font-semibold text-slate-800" : "text-slate-500"}`}>{c.last_mine && "Du: "}{c.last_text}</p>
              </div>
              {c.unread > 0 && <span className="min-w-5 h-5 px-1.5 rounded-full bg-rose-500 text-white text-[11px] font-bold flex items-center justify-center">{c.unread}</span>}
            </Link></li>
          ))}</ul>}
      </div>
    </div>
  );
}

function useThread(userId) {
  const [data, setData] = useState(null);
  const last = useRef("");
  useEffect(() => {
    let alive = true;
    last.current = "";
    const load = () => api.get(`/messages/${userId}`, { params: last.current ? { after: last.current } : {} }).then(({ data: d }) => {
      if (!alive) return;
      setData((cur) => {
        const msgs = cur ? [...cur.messages, ...d.messages.filter((m) => !cur.messages.some((x) => x.id === m.id))] : d.messages;
        last.current = msgs[msgs.length - 1]?.created_at || last.current;
        return { ...d, messages: msgs };
      });
      window.dispatchEvent(new Event("messages-read"));
    }).catch(() => {});
    load();
    const t = setInterval(load, POLL_MS);
    return () => { alive = false; clearInterval(t); };
  }, [userId]);
  const add = (m) => setData((cur) => ({ ...cur, messages: [...cur.messages, m] }));
  return { data, add };
}

export function Thread() {
  const { userId } = useParams();
  const { user } = useAuth();
  const { data, add } = useThread(userId);
  const myVip = useIsVip(user?.user_id);
  const otherVip = useIsVip(userId);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const box = useRef(null);
  const count = data?.messages.length;
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [count]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || busy) return;
    setBusy(true);
    try { const { data: m } = await api.post(`/messages/${userId}`, { text }); add(m); setText(""); }
    catch (err) { toast.error(formatErr(err)); } finally { setBusy(false); }
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  return (
    <div className="max-w-2xl mx-auto flex flex-col h-[calc(100dvh-13rem)] lg:h-[calc(100dvh-10rem)] min-h-[22rem]" data-testid="thread-page">
      <div className="flex items-center gap-3 pb-3">
        <Link to="/nachrichten" data-testid="thread-back" className="p-2 -ml-2 rounded-full hover:bg-rose-50"><ArrowLeft className="w-5 h-5" /></Link>
        <Link to={`/u/${data.user.user_id}`} className="flex items-center gap-2 min-w-0"><UserAvatar userId={data.user.user_id} name={data.user.name} picture={data.user.picture} size="w-10 h-10" /><span className="min-w-0 flex items-center gap-x-1.5 gap-y-0.5 flex-wrap"><span className={`font-bold truncate ${otherVip ? "vip-name" : ""}`} data-vip-name={otherVip || undefined}>{data.user.name}</span>{otherVip && <SupporterBadge chip testId="thread-supporter" />}</span></Link>
      </div>
      <div ref={box} className="flex-1 min-h-0 overflow-y-auto overscroll-contain rounded-3xl bg-white border border-pink-100 p-3 sm:p-4 space-y-2" data-testid="thread-messages">
        {data.messages.length === 0 && <p className="text-center text-sm text-slate-400 py-10">Schreib die erste Nachricht 👋</p>}
        {data.messages.map((m) => {
          const mine = m.from_id === user.user_id;
          const vipMsg = mine ? myVip : otherVip;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`thread-msg-${m.id}`}>
              <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${mine ? `${vipMsg ? "vip-msg-mine sp-glow sp-glow-sm" : "pink-gradient text-white"} rounded-br-md` : `${vipMsg ? "vip-msg sp-glow sp-glow-sm" : "bg-rose-50"} rounded-bl-md`}`} data-vip={vipMsg || undefined}>
                {m.story && <StoryRef s={m.story} mine={mine} />}
                {spooky(m.text, m.id)}<span className={`block text-[10px] mt-0.5 ${mine ? "text-pink-100 text-right" : "text-slate-400"}`}>{new Date(m.created_at).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            </div>
          );
        })}
      </div>
      {(data.can_reply ?? data.is_friend) ? (
        <form onSubmit={send} className="pt-3 flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Nachricht schreiben…" data-testid="thread-input"
            className="flex-1 min-w-0 h-12 px-4 rounded-full border border-pink-200 bg-white text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-rose-300" />
          <button type="submit" disabled={busy || !text.trim()} data-testid="thread-send" className="w-12 h-12 shrink-0 rounded-full pink-gradient text-white flex items-center justify-center disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}</button>
        </form>
      ) : <p className="pt-3 text-center text-sm text-slate-500" data-testid="thread-not-friends">Nachrichten kannst du nur an Freunde schicken.</p>}
    </div>
  );
}
